import React, { FC, ReactNode, useContext, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { ApolloError } from "@apollo/client";
import { GraphQLError } from "graphql";
import { TenantContext } from "v3/context/tenant";
import { LocalizationContext } from "v3/context/localization";
import { parseTemplateAsString } from "@pepdirect/v3/helpers/localization";
import {
  LoginForm as PepLoginForm,
  LoginFormValues,
} from "@pepdirect/v3/ui/LoginForm";
import { AlertTypes } from "@pepdirect/v3/ui/Alert";
import { AccountVerification } from "v3/components/portal/AccountVerification";
import { useLoginUserMutation } from "v3/services/graphql/generated";
import { logUserChange } from "v3/services/analytics";
import { getLinkFromTenant } from "v3/helpers/tenant";
import { getRedirectUrl } from "v3/helpers/routing";
import { parseLoginUserGraphQLErrors } from "@pepdirect/v3/helpers/errors";
import { LoginQueryParams } from "v3/types/auth";
import { PageRoutes } from "v3/constants/pageRoutes";

export const LoginForm: FC = () => {
  const { tenant } = useContext(TenantContext);
  const { localization } = useContext(LocalizationContext);

  const [isLoading, setIsLoading] = useState(false);
  const [emailPassword, setEmailPassword] = useState<{
    email: string;
    password: string;
  }>({ email: "", password: "" });
  const [showVerificationPage, setShowVerificationPage] = useState(false);
  const [mappedErrorMessage, setMappedErrorMessage] = useState<
    string | ReactNode
  >("");
  const [showUpdatePasswordScreen, setShowUpdatePasswordScreen] =
    useState(false);

  const [loginMutation] = useLoginUserMutation();
  const router = useRouter();

  const {
    headingText = "Login to ${tenantName}",
    headingDefaultText = "your account",
  } = localization?.forms?.signIn || {};
  const {
    invalidLinkText = "Your verification link is invalid, please log in again to verify your account.",
    tokenExpiredText = "Your verification link has expired, please log in again to verify your account.",
    verifySuccessText = "Your account has been successfully verified. You can now log in.",
  } = localization?.portal?.accountVerification || {};

  const canSignUp = tenant?.capabilities.signUp.enabled;
  const { successRedirectTo, successRedirectSite } = router.query;

  const privacyLink = getLinkFromTenant(tenant, "privacy");

  const signinPageTitle =
    tenant?.brandConfig?.customizations?.account?.signinPageTitle ??
    parseTemplateAsString(headingText, { tenantName: headingDefaultText });

  const alertDetails = useMemo<
    { text: string; alertType: AlertTypes } | undefined
  >(() => {
    if (router.query[LoginQueryParams.verified] === "false") {
      return {
        text:
          router.query[LoginQueryParams.expired] === "true"
            ? tokenExpiredText
            : invalidLinkText,
        alertType: "warning",
      };
    }
    if (router.query[LoginQueryParams.verified] === "true") {
      return {
        text: verifySuccessText,
        alertType: "success",
      };
    }
    if (router.query[LoginQueryParams.accountActivated] === "true") {
      return {
        text: "Your account has been successfully activated. You can now log in.",
        alertType: "success",
      };
    }
    if (router.query[LoginQueryParams.passwordReset] === "true") {
      return {
        text: "Your password has been successfully reset. You can now log in.",
        alertType: "success",
      };
    }
    return;
  }, [invalidLinkText, router, tokenExpiredText, verifySuccessText]);

  const onSubmit = async ({ email, password }: LoginFormValues) => {
    setEmailPassword({ email, password });
    setIsLoading(true);
    try {
      const { data } = await loginMutation({
        variables: { email, password },
      });
      logUserChange(data?.loginUser?.id);
      const redirectUrl = getRedirectUrl(
        successRedirectTo,
        successRedirectSite,
        tenant?.fallbackUrl || ""
      );
      router.push(redirectUrl || PageRoutes.account);
    } catch (e) {
      setIsLoading(false);

      if (e instanceof ApolloError && e.graphQLErrors.length) {
        const { errorMsg, passwordResetRequired, emailVerificationRequired } =
          parseLoginUserGraphQLErrors(
            e.graphQLErrors as GraphQLError[],
            localization?.forms?.signIn
          );

        if (passwordResetRequired) {
          setShowUpdatePasswordScreen(true);
        } else if (emailVerificationRequired) {
          setShowVerificationPage(true);
        } else {
          setMappedErrorMessage(errorMsg);
        }
      }
    }
  };

  const goToForgotPassword = () => {
    router.push({
      pathname: "/forgot-password",
      query: {
        ...(successRedirectTo && { successRedirectTo }),
        ...(successRedirectSite && { successRedirectSite }),
      },
    });
  };

  const goToCreateAccount = () => {
    const { successRedirectTo, successRedirectSite } = router.query;
    router.push({
      pathname: PageRoutes.signUp,
      query: {
        ...(successRedirectTo && { successRedirectTo }),
        ...(successRedirectSite && { successRedirectSite }),
      },
    });
  };

  return (
    <>
      {showVerificationPage ? (
        <AccountVerification {...emailPassword} />
      ) : (
        <PepLoginForm
          alertDetails={alertDetails}
          canSignUp={canSignUp}
          customTexts={tenant?.brandConfig.customTexts}
          error={mappedErrorMessage}
          goToCreateAccount={goToCreateAccount}
          goToForgotPassword={goToForgotPassword}
          isLoading={isLoading}
          localization={localization}
          onSubmit={onSubmit}
          privacyUrl={privacyLink?.url}
          signinPageTitle={signinPageTitle}
          showUpdatePasswordScreen={showUpdatePasswordScreen}
          tenantName={tenant?.brandConfig.title}
        />
      )}
    </>
  );
};
